import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { Locale } from '@mrigl/shared';
import { Alert, Button, TextAreaField, TextField } from '@mrigl/ui';
import { useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import {
  cleanText,
  display,
  storeLocales,
  useStore,
  type Localized,
  type StoreDetails,
} from '../catalog/shared.js';

type State = Schemas['InvoiceSettingsState'];
type Settings = Schemas['InvoiceSettings'];

const PREFIX = /^[A-Za-z0-9/-]{0,12}$/;

function invoiceError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'version-conflict') return t('admin.invoice.error.version-conflict');
    if (failure.code === 'validation-failed') return t('admin.invoice.error.validation');
  }
  return errorMessage(t, failure);
}

/** Seller details printed on invoices: entered by the merchant, printed as is. */
export function InvoiceSettingsPage() {
  const { storeId = '' } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [state, setState] = useState<State | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/invoice-settings', {
        params: { path: { storeId } },
      }),
    )
      .then(setState)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || state === null) return <Loading />;
  return <Editor store={store} initial={state} />;
}

function Editor({ store, initial }: { readonly store: StoreDetails; readonly initial: State }) {
  const { t, locale } = useI18n();
  const locales = storeLocales(store);
  const canManage = store.permissions.includes('checkout.manage');
  const [version, setVersion] = useState(initial.version);
  const [legalName, setLegalName] = useState<Localized>(initial.settings.legalName);
  const [address, setAddress] = useState<Localized>(initial.settings.address);
  const [footer, setFooter] = useState<Localized>(initial.settings.footer);
  const [taxId, setTaxId] = useState(initial.settings.taxId ?? '');
  const [prefix, setPrefix] = useState(initial.settings.numberPrefix);
  const [prefixError, setPrefixError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const languageName = (code: Locale) => t(`admin.language.${code}`);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    if (!PREFIX.test(prefix.trim())) {
      setPrefixError(t('admin.invoice.invalidPrefix'));
      setError(t('admin.invoice.error.validation'));
      return;
    }
    setPrefixError(undefined);
    const settings: Settings = {
      schemaVersion: 1,
      legalName: cleanText(legalName),
      address: cleanText(address),
      taxId: taxId.trim() === '' ? null : taxId.trim(),
      footer: cleanText(footer),
      numberPrefix: prefix.trim(),
    };
    setBusy(true);
    try {
      const saved = await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/invoice-settings', {
          params: { path: { storeId: store.storeId } },
          body: { settings, version: Math.max(version, 1) },
        }),
      );
      setVersion(saved.version);
      setNotice(t('admin.invoice.saved'));
    } catch (failure) {
      setError(invoiceError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}`}>
          {display(store.name, locale, store, store.slug)}
        </Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.invoice.title')}</h1>
      <p>{t('admin.invoice.intro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-stack"
        aria-label={t('admin.invoice.title')}
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.invoice.title')}</legend>
          {locales.map((code) => (
            <TextField
              key={`name-${code}`}
              lang={code}
              dir="auto"
              label={t('admin.invoice.legalNameIn', { language: languageName(code) })}
              maxLength={200}
              required={code === store.defaultLocale}
              value={legalName[code] ?? ''}
              onChange={(event) => {
                setLegalName({ ...legalName, [code]: event.target.value });
              }}
            />
          ))}
          {locales.map((code) => (
            <TextAreaField
              key={`address-${code}`}
              lang={code}
              dir="auto"
              label={t('admin.invoice.addressIn', { language: languageName(code) })}
              maxLength={500}
              required={code === store.defaultLocale}
              value={address[code] ?? ''}
              onChange={(event) => {
                setAddress({ ...address, [code]: event.target.value });
              }}
            />
          ))}
          <TextField
            label={t('admin.invoice.taxId')}
            dir="ltr"
            maxLength={40}
            value={taxId}
            onChange={(event) => {
              setTaxId(event.target.value);
            }}
          />
          <TextField
            label={t('admin.invoice.prefix')}
            hint={t('admin.invoice.prefixHint')}
            dir="ltr"
            maxLength={12}
            value={prefix}
            error={prefixError}
            onChange={(event) => {
              setPrefix(event.target.value);
            }}
          />
          {locales.map((code) => (
            <TextAreaField
              key={`footer-${code}`}
              lang={code}
              dir="auto"
              label={t('admin.invoice.footerIn', { language: languageName(code) })}
              maxLength={1000}
              value={footer[code] ?? ''}
              onChange={(event) => {
                setFooter({ ...footer, [code]: event.target.value });
              }}
            />
          ))}
        </fieldset>
        {canManage ? (
          <Button type="submit" busy={busy}>
            {t('admin.save')}
          </Button>
        ) : null}
      </form>
    </div>
  );
}
